import { Button } from '@talentmesh/ui/components/button';
import { Spinner } from '@talentmesh/ui/components/spinner';
import { useState } from 'react';
import { toast } from 'sonner';
import useAISearchCounters from '../../api/useAISearchCounters';
import { useHideIdentifiedProfiles, useShortlistIdentifiedProfiles } from '../../api/useIdentifiedProfiles';
import { useIdentifiedListContext } from './utils/IdentifiedListContext';
import { useIdentifiedTableContext } from './utils/IdentifiedTableContext';
import UIStrings from '@/Utils/UIStrings';

interface IdentifiedButtonActionsProps {
    selectedRowIds: Array<string>;
    singleSelectionMode?: boolean;
    closeDialogCallback?: () => void;
}

const IdentifiedButtonActions = ({
    selectedRowIds,
    singleSelectionMode = false,
    closeDialogCallback,
}: IdentifiedButtonActionsProps) => {
    const { mutate: mutateIdentifiedProfiles, isLoading: isIdentifiedProfilesLoading } = useIdentifiedListContext();
    const { trigger: triggerShortlist, isMutating: isShortlistIdentifiedProfilesMutating } =
        useShortlistIdentifiedProfiles();
    const { trigger: triggerHide, isMutating: isHideIdentifiedProfilesMutating } = useHideIdentifiedProfiles();
    const { isLoading: isAISearchCountersLoading } = useAISearchCounters();
    const { resetRowSelection } = useIdentifiedTableContext();
    const [isOutOfView, setIsOutOfView] = useState<boolean>(false);

    const handleShortlist = async () => {
        await triggerShortlist({ identifiedProfilesIds: selectedRowIds });
        toast.success(UIStrings.NumberOfCandidatesAddedToShortlist(selectedRowIds.length));

        if (singleSelectionMode) {
            setIsOutOfView(true);
        }

        mutateIdentifiedProfiles();
        resetRowSelection(true);

        if (closeDialogCallback) {
            closeDialogCallback();
        }
    };

    const handleHide = async () => {
        await triggerHide({ identifiedProfilesIds: selectedRowIds });
        toast.success(UIStrings.NumberOfCandidateStatus(selectedRowIds.length, UIStrings.Hidden));

        if (singleSelectionMode) {
            setIsOutOfView(true);
        }

        mutateIdentifiedProfiles();
        resetRowSelection(true);

        if (closeDialogCallback) {
            closeDialogCallback();
        }
    };

    const isLoading = isIdentifiedProfilesLoading || isAISearchCountersLoading;
    const isShortlistOngoing = isShortlistIdentifiedProfilesMutating || isLoading;
    const isHideOngoing = isHideIdentifiedProfilesMutating || isLoading;

    return (
        <>
            <Button size="sm" variant="default" onClick={handleShortlist} disabled={isHideOngoing || isOutOfView}>
                {isShortlistOngoing && <Spinner className="mr-1 h-3 w-3" />}
                {UIStrings.Shortlist}
            </Button>
            <Button size="sm" variant="outline" onClick={handleHide} disabled={isShortlistOngoing || isOutOfView}>
                {isHideOngoing && <Spinner className="mr-1 h-3 w-3" />}
                {UIStrings.Hide}
            </Button>
        </>
    );
};

export default IdentifiedButtonActions;
